'use client';

import { useCallback, useEffect, useState } from 'react';

import { Dialog } from '@base-ui/react/dialog';
import Link from 'next/link';

import { Logo } from './logo';
import { MENU_PANEL_LINKS, NAV_GROUPS, SIGN_IN_HREF } from './nav-data';

import { trackButtonClick } from '@/lib/analytics/track';
import { signOut, useUserSession } from '@/lib/auth/user-session';
import { opendoorUrl } from '@/lib/urls';

const ACCOUNT_HREF = opendoorUrl('/account');

const ROW_CLASS =
  'flex w-full items-center justify-between gap-3 border-b border-line-soft px-6 py-4 text-left text-ink transition hover:bg-state-hover text-body-lg';

function ChevronRight() {
  return (
    <svg aria-hidden width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
      <path
        d="M6 4l4 4-4 4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function MobileNav() {
  const [open, setOpen] = useState(false);
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  // Keep the most recently opened submenu mounted so it can slide back out.
  const [activeSubMenu, setActiveSubMenu] = useState<string | null>(null);
  const auth = useUserSession();

  useEffect(() => {
    if (openSubMenu) setActiveSubMenu(openSubMenu);
  }, [openSubMenu]);

  const close = useCallback(() => {
    setOpen(false);
    setOpenSubMenu(null);
  }, []);
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setOpenSubMenu(null);
  }, []);
  const handleSignInClick = useCallback(() => {
    trackButtonClick('account-section-click');
    close();
  }, [close]);
  const handleAccountClick = useCallback(() => {
    trackButtonClick('account-link');
    close();
  }, [close]);
  const handleSignOut = useCallback(() => {
    trackButtonClick('account-signout');
    close();
    void signOut();
  }, [close]);

  const showSubMenu = openSubMenu !== null;
  const activeGroup = NAV_GROUPS.find((group) => group.label === activeSubMenu);

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger
        aria-label="Open navigation menu"
        className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink transition hover:bg-state-hover md:hidden"
      >
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
          <path
            d="M3 6h16M3 11h16M3 16h16"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-50 bg-black/20 transition-opacity duration-300 data-[ending-style]:opacity-0 data-[starting-style]:opacity-0" />
        <Dialog.Popup className="fixed inset-0 z-50 flex flex-col bg-canvas transition-transform duration-300 ease-out data-[ending-style]:translate-x-full data-[starting-style]:translate-x-full">
          <Dialog.Title className="sr-only">Menu</Dialog.Title>

          <div className="flex shrink-0 items-center justify-between px-6 py-4">
            <Logo className="inline-flex items-center text-ink" />
            <div className="flex items-center gap-4">
              {auth.kind !== 'authenticated' && (
                <Link
                  href={SIGN_IN_HREF}
                  prefetch={false}
                  className="font-medium text-ink text-body-md"
                  onClick={handleSignInClick}
                >
                  Sign in
                </Link>
              )}
              <Dialog.Close
                aria-label="Close menu"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full text-ink transition hover:bg-state-hover"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M5 5l10 10M15 5L5 15"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </Dialog.Close>
            </div>
          </div>

          <div className="relative flex-1 overflow-hidden">
            {/* Main list */}
            <div
              aria-hidden={showSubMenu}
              inert={showSubMenu || undefined}
              className={`absolute inset-0 overflow-y-auto transition-transform duration-300 ease-in-out ${
                showSubMenu ? '-translate-x-full' : 'translate-x-0'
              }`}
            >
              <p className="bg-canvas-muted px-6 py-3 font-medium text-ink-muted text-sm">
                Resources
              </p>
              <nav aria-label="Primary mobile">
                {NAV_GROUPS.map((group) =>
                  group.columns ? (
                    <button
                      key={group.label}
                      type="button"
                      className={ROW_CLASS}
                      onClick={() => setOpenSubMenu(group.label)}
                    >
                      {group.label}
                      <ChevronRight />
                    </button>
                  ) : (
                    <Link
                      key={group.label}
                      href={group.href ?? '#'}
                      prefetch={false}
                      className={ROW_CLASS}
                      onClick={close}
                    >
                      {group.label}
                    </Link>
                  ),
                )}
                {MENU_PANEL_LINKS.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    prefetch={false}
                    className={ROW_CLASS}
                    onClick={close}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>

              {auth.kind === 'authenticated' && (
                <div>
                  <Link
                    href={ACCOUNT_HREF}
                    prefetch={false}
                    className={ROW_CLASS}
                    onClick={handleAccountClick}
                  >
                    Account
                  </Link>
                  <button type="button" className={ROW_CLASS} onClick={handleSignOut}>
                    Sign out
                  </button>
                </div>
              )}
            </div>

            {/* Drill-in sub menu */}
            {activeGroup && (
              <div
                aria-hidden={!showSubMenu}
                inert={!showSubMenu || undefined}
                className={`absolute inset-0 overflow-y-auto transition-transform duration-300 ease-in-out ${
                  showSubMenu ? 'translate-x-0' : 'translate-x-full'
                }`}
              >
                <button
                  type="button"
                  className="flex w-full items-center gap-2 border-b border-line-soft px-6 py-4 text-left font-medium text-ink transition hover:bg-state-hover text-body-md"
                  onClick={() => setOpenSubMenu(null)}
                >
                  <svg aria-hidden width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path
                      d="M10 4l-4 4 4 4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  {activeGroup.label}
                </button>
                {activeGroup.columns?.map((column) => (
                  <div key={column.heading} className="border-b border-line-soft py-2">
                    <p className="px-6 pt-2 pb-1 text-sm font-medium text-ink-faint">
                      {column.heading}
                    </p>
                    {column.links.map((item) => (
                      <Link
                        key={item.href}
                        href={item.href}
                        prefetch={false}
                        className="block px-6 py-2.5 text-ink transition hover:bg-state-hover text-body-md"
                        onClick={close}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
